import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import Breadcrumb from "../../components/ui/Breadcrumb";
import PatientInfoForm from "./components/PatientInfoForm";
import PhotoUploadZone from "./components/PhotoUploadZone";
import Icon from "../../components/AppIcon";
import { inferImage } from "../../lib/api";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { motion, AnimatePresence } from "framer-motion";

// ** VIDEO SCAN **
import VideoScanInterface from "./components/VideoScanInterface";

const NewAnalysis = () => {
  const navigate = useNavigate();

  /* ----------------------------------
     FORM STATES
  ---------------------------------- */
  const [patientName, setPatientName] = useState("");
  const [notes, setNotes] = useState("");
  const [analysisDate, setAnalysisDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const [uploadedFile, setUploadedFile] = useState(null);
  const [preview, setPreview] = useState(null);

  /* ----------------------------------
     IMAGE CROP
  ---------------------------------- */
  const [showCropModal, setShowCropModal] = useState(false);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [isSavingCrop, setIsSavingCrop] = useState(false);
  const imgRef = useRef(null);

  /* ----------------------------------
     STATES
  ---------------------------------- */
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);

  const [useVideoMode, setUseVideoMode] = useState(false);
  const [errors, setErrors] = useState({});
  const [limitReached, setLimitReached] = useState(false);
  const DAILY_LIMIT = 10;

  /* ----------------------------------
     DAILY LIMIT CHECK
  ---------------------------------- */
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    const key = `demoUsage_${today}`;
    const count = Number(localStorage.getItem(key) || 0);
    if (count >= DAILY_LIMIT) setLimitReached(true);
  }, []);

  /* ----------------------------------
     VALIDATION
  ---------------------------------- */
  const validateForm = () => {
    const newErrors = {};

    if (!patientName.trim()) newErrors.patientName = "Patient name is required";
    if (!analysisDate) newErrors.analysisDate = "Analysis date is required";

    if (!useVideoMode && !uploadedFile)
      newErrors.file = "Please upload a patient photo";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ----------------------------------
     IMAGE PREVIEW
  ---------------------------------- */
  const handleFileSelect = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  /* ----------------------------------
     CROP IMAGE
  ---------------------------------- */
  const createCroppedImage = async (image, crop) => {
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) =>
          resolve(new File([blob], "cropped_image.jpg", { type: "image/jpeg" })),
        "image/jpeg",
        0.95
      );
    });
  };

  const handleCropConfirm = async () => {
    if (!completedCrop || !imgRef.current) {
      setShowCropModal(false);
      return;
    }

    setIsSavingCrop(true);

    try {
      const newFile = await createCroppedImage(imgRef.current, completedCrop);
      const newPreviewUrl = URL.createObjectURL(newFile);

      setUploadedFile(newFile);
      setPreview(newPreviewUrl);
      setShowCropModal(false);

      setCrop(undefined);
      setCompletedCrop(null);
    } catch (e) {
      console.error("Crop error", e);
    } finally {
      setIsSavingCrop(false);
    }
  };

  /* ----------------------------------
     IMAGE ANALYSIS
  ---------------------------------- */
  const handleStartImageAnalysis = async () => {
    if (!validateForm()) return;
    if (useVideoMode) return;

    const today = new Date().toISOString().split("T")[0];
    const key = `demoUsage_${today}`;
    const used = Number(localStorage.getItem(key) || 0);

    if (used >= DAILY_LIMIT) {
      setLimitReached(true);
      return;
    }

    localStorage.setItem(key, used + 1);

    try {
      setIsAnalyzing(true);
      setAnalysisProgress(10);

      const apiResponse = await inferImage({
        imageFile: uploadedFile,
        patientName,
      });

      for (let i = 10; i <= 100; i += 10) {
        await new Promise((r) => setTimeout(r, 150));
        setAnalysisProgress(i);
      }

      await new Promise((r) => setTimeout(r, 400));
      setIsFinishing(true);
      await new Promise((r) => setTimeout(r, 450));

      navigate("/analysis-result", {
        state: { ...apiResponse, patientName, notes, analysisDate },
      });
    } catch (err) {
      console.error("Analysis Error:", err);
      setErrors({ api: err.message || "Analysis failed, please try again." });
      setIsAnalyzing(false);
    }
  };

  /* ----------------------------------
     VIDEO ANALYSIS → FIXED ROUTE
  ---------------------------------- */
  const handleVideoAnalysisComplete = (result) =>
    navigate(`/analysis-result-video/${result.jobId}`, {
      state: {
        jobId: result.jobId,
        patientName,
        notes,
        analysisDate,
        videoKey: result.videoKey,
      },
    });

  /* ----------------------------------
     RENDER UI
  ---------------------------------- */

  const breadcrumbItems = [
    { label: "Dashboard", path: "/dashboard" },
    { label: "New Analysis", path: "/new-analysis" },
  ];

  const steps = [
    { done: patientName?.trim() },
    { done: useVideoMode || uploadedFile },
    { done: patientName?.trim() && (useVideoMode || uploadedFile) },
  ];

  const completion = Math.round(
    (steps.filter((s) => s.done).length / steps.length) * 100
  );

  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      {/* BG */}
      <div
        className="fixed inset-0 -z-10 bg-[url('/assets/images/medical.jpg')] bg-cover bg-center bg-no-repeat"
      />
      <div className="fixed inset-0 -z-10 bg-black/75" />

      {/* Content */}
      <motion.div
        className="relative z-10 min-h-screen overflow-y-auto pb-24"
        animate={{
          opacity: isFinishing ? 0 : 1,
          filter: isFinishing ? "blur(12px)" : "blur(0px)",
          scale: isFinishing ? 0.98 : 1,
        }}
        transition={{ duration: 0.45 }}
      >
        <Header />

        {/* VIDEO MODE */}
        {useVideoMode && (
          <VideoScanInterface
            patientName={patientName}
            notes={notes}
            analysisDate={analysisDate}
            onComplete={handleVideoAnalysisComplete}
            onCancel={() => setUseVideoMode(false)}
          />
        )}

        {/* IMAGE MODE */}
        {!useVideoMode && (
          <main className="pt-10 px-6">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              {/* HEADER */}
              <div className="mt-8 bg-white/[0.04] backdrop-blur-lg border border-white/10 rounded-3xl p-8 shadow-xl">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 rounded-xl bg-blue-500/15 border border-blue-400/20 flex items-center justify-center">
                        <Icon name="Microscope" size={18} color="#60A5FA" />
                      </div>
                      <h1 className="text-2xl text-white font-light tracking-tight">
                        New Hair Transplant Analysis
                      </h1>
                    </div>
                    <p className="text-slate-400 text-sm mt-1 ml-12">
                      Enter patient details and upload the scalp image to begin AI-powered analysis.
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <span className="text-2xl font-light text-white">{completion}%</span>
                    <p className="text-xs text-white/30 mt-0.5">complete</p>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="w-full h-1.5 bg-white/8 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500 bg-gradient-to-r from-blue-500 to-cyan-400"
                      style={{ width: `${completion}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Patient Info */}
              <div className="mt-6 bg-white/[0.04] backdrop-blur-lg border border-white/10 rounded-3xl shadow-xl">
                <div className="px-6 py-4 border-b border-white/8 flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-blue-500/20 border border-blue-400/30 flex items-center justify-center">
                    <span className="text-blue-300 text-xs font-semibold">1</span>
                  </div>
                  <h2 className="text-white text-base font-medium">Patient Information</h2>
                </div>

                <div className="p-6 text-white">
                  <PatientInfoForm
                    patientName={patientName}
                    setPatientName={setPatientName}
                    notes={notes}
                    setNotes={setNotes}
                    analysisDate={analysisDate}
                    setAnalysisDate={setAnalysisDate}
                    errors={errors}
                  />
                </div>
              </div>

              {/* Photo Guidelines */}
              <div className="mt-6 bg-white/[0.04] backdrop-blur-lg border border-white/10 rounded-3xl shadow-xl">
                <div className="px-6 py-4 border-b border-white/8 flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-amber-500/15 border border-amber-400/25 flex items-center justify-center">
                    <Icon name="Info" size={13} color="#FCD34D" />
                  </div>
                  <h2 className="text-white text-base font-medium">Photo Guidelines</h2>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[
                      { icon: "Sun", text: "Even lighting, no shadows" },
                      { icon: "Eye", text: "Full graft area visible" },
                      { icon: "ZoomIn", text: "Sharp, no blur" },
                      { icon: "Crop", text: "Crop unrelated areas" },
                      { icon: "Aperture", text: "Avoid scalp reflections" },
                      { icon: "Image", text: "JPG / PNG · max 10MB" },
                    ].map(({ icon, text }) => (
                      <div key={text} className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-white/5 border border-white/8">
                        <Icon name={icon} size={14} color="#94A3B8" />
                        <span className="text-slate-300 text-xs leading-snug">{text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Upload Image / Switch to Video */}
              <div className="mt-6 bg-white/[0.04] backdrop-blur-lg border border-white/10 rounded-3xl shadow-xl">
                <div className="px-6 py-4 border-b border-white/8 flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-blue-500/20 border border-blue-400/30 flex items-center justify-center">
                    <span className="text-blue-300 text-xs font-semibold">2</span>
                  </div>
                  <h2 className="text-white text-base font-medium">Choose Analysis Method</h2>
                </div>

                <div className="p-6">
                  <PhotoUploadZone
                    uploadedFile={uploadedFile}
                    setUploadedFile={setUploadedFile}
                    uploadProgress={uploadProgress}
                    setUploadProgress={setUploadProgress}
                    isUploading={isUploading}
                    setIsUploading={setIsUploading}
                    errors={errors}
                    setErrors={setErrors}
                    onFileSelect={handleFileSelect}
                    onVideoModeSelect={() => setUseVideoMode(true)}
                  />

                  {/* Crop button for images */}
                  {preview && uploadedFile?.type?.startsWith("image/") && (
                    <div className="flex justify-end mt-5">
                      <button
                        onClick={() => setShowCropModal(true)}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/8 border border-white/15 text-white/70 text-sm hover:bg-white/15 hover:text-white transition"
                      >
                        <Icon name="Crop" size={14} />
                        Crop Image
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Start Analysis */}
              {!useVideoMode && (patientName?.trim() || uploadedFile) && (
                <div className="mt-6 bg-white/[0.04] backdrop-blur-lg border border-white/10 rounded-3xl shadow-xl">
                  <div className="px-6 py-4 border-b border-white/8 flex items-center gap-3">
                    <div className="w-7 h-7 rounded-lg bg-blue-500/20 border border-blue-400/30 flex items-center justify-center">
                      <span className="text-blue-300 text-xs font-semibold">3</span>
                    </div>
                    <h2 className="text-white text-base font-medium">Start Analysis</h2>
                  </div>

                  <div className="p-8 flex flex-col items-center gap-4">
                    <motion.button
                      onClick={handleStartImageAnalysis}
                      disabled={isAnalyzing || limitReached}
                      className="group flex items-center gap-3 px-10 py-4 rounded-2xl
                        bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed
                        text-white font-medium text-base
                        shadow-[0_8px_30px_rgba(37,99,235,0.35)] hover:shadow-[0_8px_40px_rgba(37,99,235,0.5)]
                        transition-all duration-200"
                      whileTap={{ scale: 0.97 }}
                    >
                      {isAnalyzing ? (
                        <>
                          <Icon name="Loader2" size={20} className="animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Icon name="Zap" size={20} />
                          Run AI Analysis
                        </>
                      )}
                    </motion.button>

                    {limitReached ? (
                      <p className="text-red-400 text-xs">
                        Daily limit reached ({DAILY_LIMIT} analyses)
                      </p>
                    ) : (
                      <p className="text-white/25 text-xs">
                        {Number(localStorage.getItem(`demoUsage_${new Date().toISOString().split("T")[0]}`) || 0)}/{DAILY_LIMIT} analyses used today
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* API Error */}
              {errors.api && (
                <div className="mt-8 bg-red-500/20 text-red-300 border border-red-500/40 rounded-xl p-4">
                  <p className="font-medium">Error</p>
                  <p className="text-sm">{errors.api}</p>
                </div>
              )}
            </div>
          </main>
        )}
      </motion.div>

      {/* CROP MODAL */}
      <AnimatePresence>
        {showCropModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#111] border border-white/10 rounded-2xl w-full max-w-5xl shadow-2xl flex flex-col max-h-[95vh]"
            >
              <div className="p-4 border-b border-white/10 flex justify-between items-center bg-[#1a1a1a] shrink-0">
                <h3 className="text-white text-lg font-semibold flex items-center">
                  <Icon name="Crop" size={20} className="mr-2 text-white" />
                  Crop Image
                </h3>
                <p className="text-slate-400 text-xs hidden sm:block">
                  Draw a rectangle to crop
                </p>
              </div>

              <div className="flex-1 bg-black/50 overflow-hidden relative flex items-center justify-center p-4">
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  className="max-w-full"
                >
                  <img
                    ref={imgRef}
                    src={preview}
                    alt="Crop Preview"
                    style={{
                      maxHeight: "70vh",
                      maxWidth: "100%",
                      objectFit: "contain",
                    }}
                  />
                </ReactCrop>
              </div>

              <div className="p-4 border-t border-white/10 bg-[#1a1a1a] flex justify-end gap-3 shrink-0">
                <button
                  onClick={() => setShowCropModal(false)}
                  className="px-4 py-2 rounded-lg text-sm text-slate-300 bg-white/5 hover:bg-white/10 transition"
                >
                  Cancel
                </button>

                <button
                  onClick={handleCropConfirm}
                  disabled={!completedCrop?.width || !completedCrop?.height}
                  className={`
                    px-5 py-2 rounded-lg text-sm font-semibold border border-white/20 text-white transition
                    ${
                      !completedCrop?.width || !completedCrop?.height
                        ? "bg-white/5 opacity-50 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-500 border-transparent shadow-lg"
                    }
                  `}
                >
                  {isSavingCrop ? "Saving..." : "Save Crop"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NewAnalysis;
